import { useState } from 'react';
import { briefToMarkdown, type BlipResult } from '../../lib/blip';
import { blipPermalink } from '../../lib/site';

function formatCost(usd: number): string {
  if (usd <= 0) return '0';
  if (usd < 0.01) return usd.toFixed(4);
  return usd.toFixed(2);
}

function useCopy(): [string | null, (id: string, text: string) => void] {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (id: string, text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(id);
        setTimeout(() => setCopied(null), 1800);
      })
      .catch(() => undefined); // clipboard blocked - no-op
  };
  return [copied, copy];
}

export function BriefView({
  result,
  sourceUrl,
}: {
  result: BlipResult;
  sourceUrl?: string | null;
}) {
  const { brief } = result;
  const [copied, copy] = useCopy();
  const permalink = result.url || (result.slug ? blipPermalink(result.slug) : null);

  return (
    <section className="mt-6 space-y-5 animate-rise-in">
      <article className="rounded-xl border border-edge bg-darker/80 p-6 sm:p-8">
        <h2 className="wordmark text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
          {brief.title}
        </h2>

        <p className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-xs text-muted">
          {brief.reading_time_min > 0 && (
            <span>
              {brief.reading_time_min} min read{sourceUrl || result.kind === 'url' ? ' at source' : ''}
            </span>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="max-w-full truncate text-teal hover:underline"
            >
              {sourceUrl}
            </a>
          )}
        </p>

        {brief.tl_dr && (
          <p className="mt-5 border-l-2 border-clay/50 pl-4 text-base leading-relaxed text-ink-soft">
            {brief.tl_dr}
          </p>
        )}

        <h3 className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-muted">
          Key points
        </h3>

        {brief.key_points.length === 0 ? (
          <p className="mt-4 text-sm italic text-muted">
            No key points came back for this source.
          </p>
        ) : (
          <ol className="mt-4 space-y-6">
            {brief.key_points.map((kp, i) => (
              <li key={i} className="flex gap-4">
                <span className="mt-1 shrink-0 font-mono text-xs tabular-nums text-clay">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[0.95rem] leading-relaxed text-ink-soft">{kp.point}</p>
                  {kp.quote && (
                    <blockquote className="mt-2.5 border-l border-edge pl-3.5">
                      <p className="text-sm italic leading-relaxed text-muted">
                        &ldquo;{kp.quote}&rdquo;
                      </p>
                      {/* The claim and the tick are ONE fact, gated together. Today the backend
                          never sends a quote without quote_verified true (an unverified quote is
                          nulled out server-side, see _blip_lib.php's briefblipNormalizeBrief), so
                          this can't currently render a false claim -- but that's an invariant this
                          component doesn't own. If a future change ever surfaces an unverified
                          quote for display, the caption must not keep asserting it's verbatim. */}
                      {kp.quote_verified && (
                        <cite className="mt-1 flex items-center gap-1.5 font-mono text-[0.65rem] uppercase not-italic tracking-[0.14em] text-muted">
                          <svg
                            viewBox="0 0 10 10"
                            aria-hidden="true"
                            className="h-2.5 w-2.5 text-teal"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          >
                            <path d="M1.5 5.2 3.9 7.6 8.5 2.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Found verbatim in the source
                        </cite>
                      )}
                    </blockquote>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}

        {brief.facts_figures.length > 0 && (
          <>
            <h3 className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-muted">
              Facts &amp; figures
            </h3>
            <ul className="mt-4 space-y-2">
              {brief.facts_figures.map((fact, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-ink-soft">
                  <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-clay" />
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {brief.who_should_care && (
          <div className="mt-8 rounded-lg border border-teal/25 bg-teal/[0.06] p-5">
            <h3 className="font-mono text-xs uppercase tracking-[0.24em] text-teal">
              Who should care
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">{brief.who_should_care}</p>
          </div>
        )}

        {/* Provenance, stated plainly. The backend checks every quote against the
            source text and drops the ones that are not there; saying so is the
            difference between "the model cited something" and "this citation is real". */}
        {(brief.quotes_rejected > 0 || result.cost) && (
          <div className="mt-8 space-y-1 border-t border-edge pt-4 text-center font-mono text-[0.7rem] text-muted">
            {brief.quotes_rejected > 0 && (
              <p>
                {brief.quotes_rejected} quote{brief.quotes_rejected === 1 ? '' : 's'} the model
                offered did not appear in the source and {brief.quotes_rejected === 1 ? 'was' : 'were'}{' '}
                dropped.
              </p>
            )}
            {result.cost && (
              <p>
                Briefed by {result.cost.model} - ~${formatCost(result.cost.usd)}
              </p>
            )}
          </div>
        )}
      </article>

      <div className="rounded-xl border border-edge bg-darker/80 p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.24em] text-muted">Share</h3>

        {permalink ? (
          <>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <label htmlFor="blip-permalink" className="sr-only">
                Permalink to this brief
              </label>
              <input
                id="blip-permalink"
                type="text"
                readOnly
                value={permalink}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-lg border border-edge bg-white/80 px-4 py-2.5 font-mono text-xs text-ink focus:border-teal focus:outline-none"
              />
              <button
                type="button"
                onClick={() => copy('link', permalink)}
                className="shrink-0 rounded-lg border border-edge px-4 py-2.5 text-xs text-teal transition hover:bg-teal/10"
              >
                {copied === 'link' ? 'Copied!' : 'Copy link'}
              </button>
            </div>
            <p className="mt-3 font-mono text-xs text-muted">
              Anyone with the link can read this brief.
            </p>
          </>
        ) : (
          <p className="mt-4 text-sm text-muted">
            This brief was not saved, so it has no permalink. Copy the Markdown to keep it.
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2 border-t border-edge pt-4">
          <button
            type="button"
            onClick={() => copy('md', briefToMarkdown(result, sourceUrl))}
            className="rounded-md border border-edge px-3 py-1.5 text-xs text-teal transition hover:bg-teal/10"
          >
            {copied === 'md' ? 'Copied!' : 'Copy markdown'}
          </button>
          {permalink && (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-edge px-3 py-1.5 text-xs text-teal transition hover:bg-teal/10"
            >
              Open permalink
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
